import { Fragment, useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Mui, MuiIcons } from "@osu/react-ui";
import {
  selectActiveReportingPeriodPaths,
  selectRenderPaths,
} from "../../services/submissionPeriod/slices";
import { selectCanCreateForms } from "../../services/authorization/slices";
import { mergeSxStyles } from "../../util/functions";
import NavbarLink from "./Link";
import { SESSION_KINDS } from "../../util/constants";
import { useGetSessionInfoQuery } from "../../services/submissionPeriod/api";
const { Menu, MenuItem, ListItemIcon, ListItemText } = Mui;
const { Add: AddIcon } = MuiIcons;

const ids = {
  menu: "create-forms-menu",
  button: "create-forms-button",
};

const listItemSx =  ({ highlight }) => [
  (theme) => {
    const isDarkMode = theme?.palette?.mode === "dark";
    const darkGrays = theme?.palette?.brand?.gray?.darker;
    const lightGrays = theme?.palette?.brand?.gray?.lighter;
    const highlightBackgroundColor = (isDarkMode ? darkGrays : lightGrays)?.[80]
    let cls = {
      textTransform: "none",
      borderLeft: `4px solid transparent`,
      height: '100%',
      borderRadius: 0,
      padding: `.6rem .4rem .6rem 0`,
      alignSelf: "bottom",
      color: theme?.palette?.textColor,
      [`&.${Mui.buttonClasses.textPrimary}`]: {
        color: theme?.palette?.textColor,
      },
      "&:hover": {
        backgroundColor: highlightBackgroundColor,
        borderLeftColor: (isDarkMode ? lightGrays : darkGrays)?.[40],
      },
    };
    
    if(highlight) {
        cls.backgroundColor = highlightBackgroundColor
        cls.borderLeftColor = theme?.palette?.brand?.gray?.main
    }
    return cls
  }
]

function CreateSelect({ sx }) {
  const { isLoading } = useGetSessionInfoQuery({ type: SESSION_KINDS.FGC });
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const timeoutRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const paths = useSelector(
    (state) => {
      const render = selectRenderPaths(state) && selectCanCreateForms(state);
      if (!render) {
        return [];
      }
      return selectActiveReportingPeriodPaths(state);
    },
    (a, b) => {
      const hash = (arr) => (arr || []).map((a) => (a?.path || "") + (a?.title || "")).join("");
      return hash(a) === hash(b);
    }
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!paths?.length) {
    return null;
  }

  const handleClick = (event) => {
    // Clear any hover timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Toggle menu on click
    if (open) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setAnchorEl(null);
  };

  const handleMouseEnter = (event) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Only open on hover if not already open
    if (!open) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMouseLeave = () => {
    // Only set timeout if menu is open
    if (open) {
      timeoutRef.current = setTimeout(() => {
        setAnchorEl(null);
      }, 150);
    }
  };

  const handleMenuMouseEnter = () => {
    // Cancel closing when hovering over menu
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleMenuMouseLeave = () => {
    // Close menu when leaving menu area
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setAnchorEl(null);
  };

  if(isLoading) {
    return <Mui.Skeleton sx={mergeSxStyles({ ml: 0 },sx)} width="4rem" height="4rem" />
  }

  return (
    <Fragment>
      <NavbarLink
        id={ids.button}
        aria-controls={open ? ids.menu : undefined}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={mergeSxStyles({ ml: 0 },sx)}
        component={Mui.Button}
      >
        Forms
      </NavbarLink>
      <Menu
        id={ids.menu}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onMouseEnter={handleMenuMouseEnter}
        onMouseLeave={handleMenuMouseLeave}
        MenuListProps={{
          "aria-labelledby": ids.button,
          role: "menu",
          onMouseEnter: handleMenuMouseEnter,
          onMouseLeave: handleMenuMouseLeave,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            onMouseEnter: handleMenuMouseEnter,
            onMouseLeave: handleMenuMouseLeave,
          },
        }}
      >
        {paths.map(({ path, title }, index) => {
          const onPath = path === pathname
          if(!path || !title) {
            return false
          }
          return (
            <MenuItem
              key={path + index}
              role="menuitem"
              tabIndex={0}
              aria-current={onPath ? "page" : undefined}
              onClick={() => {
                navigate(path);
                handleClose();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(path);
                  handleClose();
                }
              }}
              sx={listItemSx({
                highlight: onPath
              })}
            >
              <ListItemIcon>
                <AddIcon titleAccess="Create" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={title}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Fragment>
  );
}

export default CreateSelect;
